import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CurrencySwitch } from "@/components/CurrencySwitch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const T = {
  fr: {
    eyebrow: "Sans engagement", title: "Choisis ton souffle",
    hint: "Glisse ou utilise les flèches", free: "Gratuit",
    cta: "Commencer", ctaAll: "Voir tous les détails",
    plans: [
      { key: "souffle", name: "Le Souffle", price: "0", tagline: "Goûter la magie, sans friction", feats: ["5 conversations/jour avec L'Élan, ton précepteur IA", "Ta Constellation & ton Carnet d'Épiphanies", "Le « Saviez-vous ? » du jour + tous les Domaines", "Les Conférences mensuelles + le chat en direct", "L'Arène — voter aux grands débats", "Radio Cocoon en direct, 5 chaînes"] },
      { key: "elan", name: "L'Élan", price: "7", tagline: "La conversation vraiment infinie", feats: ["Conversations illimitées avec L'Élan", "La Voix de L'Élan + la dictée vocale", "L'Import : explore une image ou une œuvre", "Les 7 Jeux de l'esprit + le Podcast du fil"], popular: true },
      { key: "cocon", name: "Le Cocoon", price: "15", tagline: "Pour toute la famille, multi-sentiers", feats: ["Compte Famille : jusqu'à 5 explorateurs (profils séparés)", "Tout L'Élan, en illimité", "La Veillée communautaire + le Cercle en direct", "L'Académie de l'IA (Chlorophylle) au complet", "Le Rituel du matin + parrainage récompensé"] },
    ],
  },
  en: {
    eyebrow: "No commitment", title: "Choose your breath",
    hint: "Swipe or use the arrows", free: "Free",
    cta: "Get started", ctaAll: "See all details",
    plans: [
      { key: "souffle", name: "The Breath", price: "0", tagline: "Taste the magic, frictionless", feats: ["5 conversations/day with L'Élan, your AI tutor", "Your Constellation & Book of Epiphanies", "The daily \"Did you know?\" + all Domains", "The monthly Conferences + live chat", "The Arena — vote in the great debates", "Radio Cocoon live, 5 channels"] },
      { key: "elan", name: "L'Élan", price: "7", tagline: "The truly infinite conversation", feats: ["Unlimited conversations with L'Élan", "The Voice of L'Élan + voice dictation", "The Import: explore an image or artwork", "The 7 Mind Games + the Thread Podcast"], popular: true },
      { key: "cocon", name: "The Cocoon", price: "15", tagline: "For the whole family, multi-path", feats: ["Family Account: up to 5 explorers (separate profiles)", "Everything in L'Élan, unlimited", "The community Vigil + the live Circle", "The full AI Academy (Chlorophylle)", "The morning ritual + rewarded referrals"] },
    ],
  },
  es: {
    eyebrow: "Sin compromiso", title: "Elige tu aliento",
    hint: "Desliza o usa las flechas", free: "Gratis",
    cta: "Empezar", ctaAll: "Ver todos los detalles",
    plans: [
      { key: "souffle", name: "El Aliento", price: "0", tagline: "Probar la magia, sin fricción", feats: ["5 conversaciones/día con L'Élan, tu preceptor IA", "Tu Constelación y tu Cuaderno de Epifanías", "El «¿Sabías que?» del día + todos los Dominios", "Las Conferencias mensuales + el chat en directo", "La Arena — votar en los grandes debates", "Radio Cocoon en directo, 5 canales"] },
      { key: "elan", name: "L'Élan", price: "7", tagline: "La conversación verdaderamente infinita", feats: ["Conversaciones ilimitadas con L'Élan", "La Voz de L'Élan + dictado por voz", "El Import: explora una imagen o una obra", "Los 7 Juegos del ingenio + el Podcast del hilo"], popular: true },
      { key: "cocon", name: "El Cocoon", price: "15", tagline: "Para toda la familia, múltiples senderos", feats: ["Cuenta Familiar: hasta 5 exploradores (perfiles separados)", "Todo L'Élan, ilimitado", "La Velada comunitaria + el Círculo en directo", "La Academia de IA (Chlorophylle) completa", "El ritual matinal + recompensas por recomendar"] },
    ],
  },
  nl: {
    eyebrow: "Zonder verplichting", title: "Kies je adem",
    hint: "Veeg of gebruik de pijlen", free: "Gratis",
    cta: "Beginnen", ctaAll: "Alle details bekijken",
    plans: [
      { key: "souffle", name: "De Adem", price: "0", tagline: "Om nieuwsgierigheid te proeven", feats: ["De curiositeit van de dag", "De maandelijkse Conferenties + livechat", "De Arena — stemmen op de grote debatten", "Essentiële spellen & lexicons"] },
      { key: "elan", name: "De Vaart", price: "7", tagline: "Om grenzeloos te verkennen", feats: ["Alles van De Adem", "Volledig Atelier & Arena", "Je onbeperkte AI-mentor"], popular: true },
      { key: "cocon", name: "De Cocoon", price: "15", tagline: "Voor het hele gezin", feats: ["Gezinsaccount: tot 5 verkenners (aparte profielen)", "Alles van De Vaart", "De volledige AI-Academie", "Het ochtendritueel + beloonde verwijzingen"] },
    ],
  },
};

export function TarifsApercu() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { convert, symbol } = useCurrency();
  const navigate = useNavigate();
  const c = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const plans = c.plans;
  const n = plans.length;
  const popularIndex = Math.max(0, plans.findIndex((p) => p.popular));

  const [active, setActive] = useState(popularIndex);
  const [annual, setAnnual] = useState(false);

  const go = useCallback((dir) => setActive((a) => (a + dir + n) % n), [n]);

  const handleCta = async (key) => {
    if (key === "souffle") { navigate("/entrer"); return; }
    if (!user) {
      localStorage.setItem("coccon_checkout_intent", `${key}_${annual ? "yearly" : "monthly"}`);
      navigate("/entrer");
      return;
    }
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: `${key}_${annual ? "yearly" : "monthly"}`, origin_url: window.location.origin });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no_url");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Le paiement n'est pas disponible pour le moment.");
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const annualTotal = (m) => Math.round(m * 12 * 0.7); // -30 %
  const annualPerMonth = (m) => (annualTotal(m) / 12).toFixed(2).replace(".", ",");

  return (
    <section className="relative px-6 my-24 overflow-hidden" data-testid="tarifs-apercu">
      {/* Lueur ambrée d'ambiance */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: "radial-gradient(ellipse at 50% 40%, hsl(28 96% 56% / 0.12), transparent 60%)" }} />
      <div className="max-w-6xl mx-auto text-center">
        <p className="text-primary/80 uppercase tracking-[0.28em] text-xs mb-3">{c.eyebrow}</p>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-5xl leading-tight pb-[0.08em] mb-8">{c.title}</h2>

        {/* Bascule Mensuel / Annuel */}
        <div className="relative inline-flex items-center mb-14 p-1 rounded-full glass" data-testid="tarifs-apercu-billing">
          <button
            data-testid="tarifs-apercu-monthly"
            onClick={() => setAnnual(false)}
            className="relative z-10 px-5 py-2 rounded-full text-sm font-medium"
          >
            {!annual && (
              <motion.span layoutId="apercu-billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                transition={{ type: "spring", stiffness: 380, damping: 32 }} />
            )}
            <span className={`transition-colors ${!annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.monthly")}</span>
          </button>
          <button
            data-testid="tarifs-apercu-annual"
            onClick={() => setAnnual(true)}
            className="relative z-10 px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2"
          >
            {annual && (
              <motion.span layoutId="apercu-billing-pill" className="absolute inset-0 -z-10 rounded-full bg-primary"
                transition={{ type: "spring", stiffness: 380, damping: 32 }} />
            )}
            <span className={`transition-colors ${annual ? "text-primary-foreground" : "text-foreground/60"}`}>{t("tarifs.annual")}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${annual ? "bg-primary-foreground/25 text-primary-foreground" : "bg-primary/20 text-primary"}`}>{t("tarifs.save")}</span>
          </button>
        </div>

        <div className="mb-14 -mt-8 flex justify-center">
          <CurrencySwitch />
        </div>

        {/* Carrousel en fondu */}
        <div className="relative select-none" style={{ perspective: 1600 }}>
          <motion.div
            className="relative mx-auto h-[588px] sm:h-[600px] max-w-md flex items-center justify-center cursor-grab active:cursor-grabbing"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60 || info.velocity.x < -300) go(1);
              else if (info.offset.x > 60 || info.velocity.x > 300) go(-1);
            }}
          >
            {plans.map((p, i) => {
              let offset = i - active;
              if (offset > n / 2) offset -= n;
              if (offset < -n / 2) offset += n;
              const isActive = offset === 0;
              const isFree = p.price === "0";
              const m = Number(p.price);
              const displayPrice = isFree ? c.free : (annual ? `${convert(annualTotal(m))} ${symbol}` : `${convert(m)} ${symbol}`);
              const period = isFree ? t("tarifs.forever") : (annual ? t("tarifs.perYear") : t("tarifs.perMonth"));

              return (
                <motion.div
                  key={p.key}
                  data-testid={`tarif-apercu-${p.key}`}
                  className={`absolute w-[340px] max-w-[86vw] left-1/2 -ml-[170px] max-[380px]:-ml-[43vw] rounded-[28px] p-8 flex flex-col text-left glass ${p.popular ? "ring-1 ring-primary/50 frame-pulse" : "ring-1 ring-white/10"}`}
                  style={{
                    backgroundImage: "radial-gradient(circle at 50% 22%, hsl(28 96% 56% / 0.30), hsl(24 95% 52% / 0.10) 44%, transparent 72%)",
                    transformOrigin: "center center",
                    pointerEvents: isActive ? "auto" : "none",
                  }}
                  animate={{
                    x: offset * 210,
                    scale: isActive ? 1 : 0.8,
                    opacity: isActive ? 1 : 0.28,
                    rotateY: offset * -22,
                    filter: isActive ? "blur(0px)" : "blur(2px)",
                    zIndex: isActive ? 30 : 20 - Math.abs(offset),
                    boxShadow: isActive
                      ? "0 46px 100px -22px rgba(0,0,0,0.8), 0 0 60px -12px hsl(28 96% 56% / 0.35)"
                      : "0 22px 60px -26px rgba(0,0,0,0.65)",
                  }}
                  transition={{ type: "spring", stiffness: 220, damping: 28, opacity: { duration: 0.5 }, filter: { duration: 0.5 } }}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shadow-[0_0_18px_hsl(28_96%_56%_/_0.5)]">
                      {t("tarifs.popular")}
                    </span>
                  )}
                  <h3 className="font-display text-2xl text-foreground/90 mb-1">{p.name}</h3>
                  <p className="text-sm text-foreground/55 italic font-display mb-5">{p.tagline}</p>
                  <div className="flex items-end gap-1.5 mb-1">
                    {isFree ? (
                      <span className="font-display text-4xl text-gradient-orange">{c.free}</span>
                    ) : (
                      <>
                        <span className="font-display text-5xl text-gradient-orange">{displayPrice}</span>
                        <span className="text-foreground/45 text-sm mb-1.5">/ {period}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-foreground/40 mb-6 h-4">
                    {annual && !isFree ? t("tarifs.billedAnnually", { m: annualPerMonth(m) }) : ""}
                  </p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {p.feats.map((f, k) => (
                      <li key={k} className="flex items-start gap-2.5 text-sm text-foreground/70">
                        <Check size={16} className="text-primary flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleCta(p.key)}
                    data-testid={`tarif-apercu-cta-${p.key}`}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm ${p.popular ? "btn-frost-orange" : "btn-glass-soft text-foreground/80 hover:text-primary"}`}
                  >
                    {c.cta} <ArrowRight size={15} />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Flèches */}
          <button
            data-testid="tarifs-apercu-prev"
            onClick={() => go(-1)}
            aria-label="Précédent"
            className="absolute left-0 sm:left-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full glass ring-1 ring-primary/25 flex items-center justify-center text-foreground/70 hover:text-primary hover:ring-primary/50 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="tarifs-apercu-next"
            onClick={() => go(1)}
            aria-label="Suivant"
            className="absolute right-0 sm:right-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full glass ring-1 ring-primary/25 flex items-center justify-center text-foreground/70 hover:text-primary hover:ring-primary/50 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Points de navigation */}
        <div className="flex items-center justify-center gap-2.5 mt-6" data-testid="tarifs-apercu-dots">
          {plans.map((p, i) => (
            <button
              key={p.key}
              data-testid={`tarifs-apercu-dot-${i}`}
              onClick={() => setActive(i)}
              aria-label={p.name}
              className={`rounded-full transition-all duration-300 ${active === i ? "w-7 h-2 bg-primary" : "w-2 h-2 bg-foreground/25 hover:bg-foreground/50"}`}
            />
          ))}
        </div>

        <p className="text-foreground/35 text-xs mt-4" data-testid="tarifs-apercu-hint">{c.hint}</p>

        <Link to="/tarifs" data-testid="tarifs-apercu-all" className="inline-flex items-center gap-2 mt-8 text-sm text-foreground/55 hover:text-primary transition-colors">
          {c.ctaAll} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
